import i18n from '@/utils/i18n';
import {
  FinanceHubUnpaidInvoiceListItemFieldsFragment,
  InvoiceListRecordType,
  InvoiceListSortDirection,
  InvoiceListSortField,
} from '@/types';
import { formatCurrency } from '@/utils/money';
import { formatDateWithYear, formatTime } from '@/utils/date';
import { InvoiceSummaryBucketFilter } from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-filters';
import {
  UnpaidInvoiceFilters,
  UnpaidInvoiceListFilterParams,
  UnpaidInvoiceListSortParams,
  UnpaidInvoiceRecordTypeFilter,
  UnpaidInvoiceRow,
  UnpaidInvoiceSort,
  UnpaidInvoiceSortField,
} from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-list';

const EMPTY_VALUE = '—';

// "all" maps to undefined: sending no record_type leaves the list unfiltered.
const RECORD_TYPE_FILTER_TO_API_RECORD_TYPE: Record<
  UnpaidInvoiceRecordTypeFilter,
  InvoiceListRecordType | undefined
> = {
  all: undefined,
  creditMemos: InvoiceListRecordType.CreditMemo,
  invoices: InvoiceListRecordType.Invoice,
};

const SORT_FIELD_TO_API_FIELD: Record<
  UnpaidInvoiceSortField,
  InvoiceListSortField
> = {
  customerName: InvoiceListSortField.CustomerName,
  daysOverdue: InvoiceListSortField.DaysOverdue,
  dueDate: InvoiceListSortField.DueAt,
  erpLastSyncedAt: InvoiceListSortField.ErpLastModifiedAt,
  invoiceNumber: InvoiceListSortField.InvoiceNumber,
  remainingBalance: InvoiceListSortField.RemainingBalance,
};

const formatAmount = (amountInMicros: number, currencyCode: string) =>
  formatCurrency(amountInMicros, currencyCode, {
    currencyDisplay: 'narrowSymbol',
  });

const getAmountPaidLabel = (
  item: FinanceHubUnpaidInvoiceListItemFieldsFragment,
) => {
  if (!item.is_partially_paid || item.amount_paid_in_micros <= 0) {
    return null;
  }

  return i18n.t('finance_hub.invoices.table.amount_paid', {
    amount: formatAmount(item.amount_paid_in_micros, item.currency_code),
  });
};

export const transformUnpaidInvoiceListItem = (
  item: FinanceHubUnpaidInvoiceListItemFieldsFragment,
): UnpaidInvoiceRow => ({
  amountPaidLabel: getAmountPaidLabel(item),
  customerName: item.customer_name ?? EMPTY_VALUE,
  daysOverdue: item.days_overdue,
  dueDateLabel: item.due_at ? formatDateWithYear(item.due_at) : EMPTY_VALUE,
  erpLastSyncedDateLabel: item.erp_last_modified_at
    ? formatDateWithYear(item.erp_last_modified_at)
    : null,
  erpLastSyncedTimeLabel: item.erp_last_modified_at
    ? formatTime(item.erp_last_modified_at)
    : null,
  invoiceNumber: item.invoice_number ?? EMPTY_VALUE,
  recordType: item.record_type,
  remainingBalanceLabel: formatAmount(
    item.remaining_balance_in_micros,
    item.currency_code,
  ),
});

/**
 * Turns the selected bucket card into list-API filter params.
 *
 * @param filter The filter the selected bucket card applies.
 * @returns The overdue bucket or record type to query with.
 */
export const serializeUnpaidInvoiceBucketFilter = (
  filter: InvoiceSummaryBucketFilter,
): UnpaidInvoiceListFilterParams => {
  switch (filter.kind) {
    case 'agingBucket':
      return { overdue_bucket: filter.overdueBucket };
    case 'allInvoices':
      return {};
    case 'creditMemos':
      return { record_type: InvoiceListRecordType.CreditMemo };
  }
};

export const serializeUnpaidInvoiceFilters = (
  filters: UnpaidInvoiceFilters,
): UnpaidInvoiceListFilterParams => ({
  record_type: RECORD_TYPE_FILTER_TO_API_RECORD_TYPE[filters.recordType],
});

/**
 * Turns what the user typed into the list API's search param.
 *
 * @param search The raw search text.
 * @returns The trimmed term, or undefined when the box holds nothing to
 *   search on — sending no term leaves the list unsearched.
 */
export const serializeUnpaidInvoiceSearch = (
  search: string,
): string | undefined => search.trim() || undefined;

export const serializeUnpaidInvoiceSort = (
  sort: UnpaidInvoiceSort,
): UnpaidInvoiceListSortParams => ({
  sort_by: SORT_FIELD_TO_API_FIELD[sort.field],
  sort_direction:
    sort.direction === 'desc'
      ? InvoiceListSortDirection.Desc
      : InvoiceListSortDirection.Asc,
});
